import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Input, BloomLogo, cn } from "@bloom/ui";
import { Loader2 } from "lucide-react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useAuth } from "../auth/auth-context.tsx";

// ─── Reduced-motion hook ─────────────────────────────────────────────────────
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

// ─── Pointer-type hook ──────────────────────────────────────────────────────
// The cursor-following orb (item E) only makes sense on devices with a real
// pointer — touch screens have no hover state. Returns true only when the
// primary input is a mouse / fine pointer.
function useHasFinePointer() {
  const [fine, setFine] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(pointer: fine)");
    setFine(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setFine(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return fine;
}

// ─── Aurora-mesh blobs (item B) ─────────────────────────────────────────────
// Four large blurred radial gradient blobs each drifting on its own loop.
// Together they read as a slow color-mesh; individually they're cheap CSS.
const AURORA_BLOBS = [
  // x/y in vw/vh, size in vw, color, duration
  { x: -10, y: -10, size: 60, color: "rgba(247,200,213,0.7)", dx: 18, dy: 12, duration: 26 },
  { x: 70, y: -5, size: 55, color: "rgba(233,75,122,0.35)", dx: -12, dy: 15, duration: 32 },
  { x: -15, y: 60, size: 50, color: "rgba(247,200,213,0.55)", dx: 16, dy: -10, duration: 28 },
  { x: 60, y: 65, size: 65, color: "rgba(255,238,243,0.9)", dx: -14, dy: -16, duration: 36 },
] as const;

// ─── Petals as real SVGs (item C) ───────────────────────────────────────────
// Five-petal cherry-blossom silhouette. Filled with currentColor so the parent
// motion.span sets the petal tone — keeps the brand pink in one place.
function PetalSvg({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden style={{ display: "block" }}>
      <path
        d="M12 2c1.4 2.4 3.6 4 6 4-.2 2.4-1.6 4.6-4 6 2.4 1.4 3.8 3.6 4 6-2.4 0-4.6 1.6-6 4-1.4-2.4-3.6-4-6-4 .2-2.4 1.6-4.6 4-6-2.4-1.4-3.8-3.6-4-6 2.4 0 4.6-1.6 6-4z"
        fill="currentColor"
        opacity="0.85"
      />
      <circle cx="12" cy="12" r="1.3" fill="rgba(0,0,0,0.06)" />
    </svg>
  );
}

const PETALS = [
  { left: "6%", size: 14, delay: 0, duration: 22, sway: 30 },
  { left: "18%", size: 10, delay: 5, duration: 28, sway: -24 },
  { left: "31%", size: 16, delay: 11, duration: 24, sway: 36 },
  { left: "44%", size: 9, delay: 3, duration: 30, sway: -18 },
  { left: "58%", size: 12, delay: 16, duration: 25, sway: 22 },
  { left: "70%", size: 18, delay: 8, duration: 32, sway: -30 },
  { left: "82%", size: 11, delay: 13, duration: 26, sway: 28 },
  { left: "92%", size: 8, delay: 19, duration: 20, sway: -16 },
] as const;

// All demo accounts share this password. Seeded by tooling/scripts/seed.ts.
const DEMO_PASSWORD = "BloomDev!2026";

const DEMO_ROLES = [
  { role: "superadmin", label: "Superadmin" },
  { role: "admin", label: "Admin" },
  { role: "content_manager", label: "Content" },
  { role: "finance", label: "Finance" },
  { role: "support", label: "Support" },
  { role: "user", label: "Viewer" },
] as const;

const demoEmailFor = (role: string) => `${role.replace(/_/g, "-")}@bloom.dev`;

export function LoginPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [showDemo, setShowDemo] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const hasFinePointer = useHasFinePointer();

  const isDev = import.meta.env.VITE_DISABLE_DEMO_LOGINS !== "true";

  // ─── Cursor-aware orb (item E) ────────────────────────────────────────────
  // useMotionValue + useSpring gives an organic lag that beats CSS transitions.
  const orbX = useMotionValue(0);
  const orbY = useMotionValue(0);
  const orbXSpring = useSpring(orbX, { stiffness: 50, damping: 20, mass: 1.2 });
  const orbYSpring = useSpring(orbY, { stiffness: 50, damping: 20, mass: 1.2 });
  // Translate the spring values back to the orb's top-left corner.
  const orbLeft = useTransform(orbXSpring, (v) => `${v - 220}px`);
  const orbTop = useTransform(orbYSpring, (v) => `${v - 220}px`);
  useEffect(() => {
    if (!hasFinePointer || reducedMotion) return;
    const onMove = (e: MouseEvent) => {
      orbX.set(e.clientX);
      orbY.set(e.clientY);
    };
    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [hasFinePointer, reducedMotion, orbX, orbY]);

  const signInAs = async (creds: { email: string; password: string }) => {
    setError(null);
    const res = await fetch("/api/auth/sign-in/email", {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(creds),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      throw new Error(body.message ?? `Login failed (${res.status})`);
    }
    await refresh();
    navigate("/");
  };

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signInAs({ email, password });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const onDemoClick = async (role: string) => {
    setPending(role);
    try {
      await signInAs({ email: demoEmailFor(role), password: DEMO_PASSWORD });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#fff8fa] via-[#fce6ee] to-[#f4cfdc]">
      {/* ── B — Aurora mesh ────────────────────────────────────────────────── */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {AURORA_BLOBS.map((b, i) => (
          <motion.span
            key={i}
            className="absolute block rounded-full"
            style={{
              width: `${b.size}vw`,
              height: `${b.size}vw`,
              left: `${b.x}vw`,
              top: `${b.y}vh`,
              background: `radial-gradient(circle, ${b.color} 0%, transparent 65%)`,
              filter: "blur(60px)",
              willChange: "transform",
            }}
            initial={false}
            animate={
              reducedMotion
                ? { x: 0, y: 0 }
                : { x: [0, b.dx, 0, -b.dx, 0], y: [0, b.dy, 2 * b.dy, b.dy, 0] }
            }
            transition={
              reducedMotion
                ? { duration: 0 }
                : { duration: b.duration, repeat: Infinity, ease: "easeInOut" }
            }
          />
        ))}
      </div>

      {/* ── A — Wordmark watermark with shimmer sweep ──────────────────────── */}
      {/* Switched fill to ink-tone at 6% so the BL∞M letters read against pink
          (rose-on-pink at 15% blurred into the gradient — your complaint).
          A linear-gradient mask sweeps a white shimmer across the wordmark
          every 9s. */}
      <motion.div
        aria-hidden
        initial={false}
        animate={reducedMotion ? { scale: 1 } : { scale: [1, 1.025, 1] }}
        transition={
          reducedMotion ? { duration: 0 } : { duration: 12, repeat: Infinity, ease: "easeInOut" }
        }
        className="pointer-events-none absolute right-[-6vw] top-1/2 hidden -translate-y-1/2 select-none lg:block"
        style={{ transformOrigin: "center" }}
      >
        <div className="relative">
          <BloomLogo
            className="h-[36vw] max-h-[40rem] w-auto"
            style={{ color: "rgba(14,14,16,0.07)" }}
          />
          {/* Shimmer overlay — clipped to the logo's bounding box via the
              parent. A diagonal white-gradient stripe slides L→R every 9s. */}
          {!reducedMotion ? (
            <motion.div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "linear-gradient(110deg, transparent 0%, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%, transparent 100%)",
                backgroundSize: "200% 100%",
                mixBlendMode: "screen",
              }}
              animate={{ backgroundPositionX: ["-100%", "100%"] }}
              transition={{
                duration: 9,
                repeat: Infinity,
                repeatDelay: 4,
                ease: "easeInOut",
              }}
            />
          ) : null}
        </div>
      </motion.div>

      {/* ── E — Cursor-following gradient orb ──────────────────────────────── */}
      {hasFinePointer && !reducedMotion ? (
        <motion.div
          aria-hidden
          className="pointer-events-none fixed z-0 hidden h-[440px] w-[440px] rounded-full lg:block"
          style={{
            left: orbLeft,
            top: orbTop,
            background:
              "radial-gradient(circle, rgba(233,75,122,0.32) 0%, rgba(247,200,213,0.18) 35%, transparent 70%)",
            filter: "blur(40px)",
            willChange: "transform",
          }}
        />
      ) : null}

      {/* ── C — Petals as real SVGs, denser, varied ────────────────────────── */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        {PETALS.map((p, i) => (
          <motion.span
            key={i}
            className="absolute top-[-8vh] block text-[var(--brand-pink)]"
            style={{ left: p.left, willChange: "transform" }}
            initial={false}
            animate={
              reducedMotion
                ? { y: "20vh", rotate: 0, opacity: 0.25, x: 0 }
                : {
                    y: ["0vh", "110vh"],
                    rotate: [0, 360],
                    x: [0, p.sway, -p.sway, p.sway, 0],
                    opacity: [0, 0.55, 0.55, 0.3, 0],
                  }
            }
            transition={
              reducedMotion
                ? { duration: 0 }
                : {
                    duration: p.duration,
                    repeat: Infinity,
                    delay: p.delay,
                    ease: "easeInOut",
                  }
            }
          >
            <PetalSvg size={p.size} />
          </motion.span>
        ))}
      </div>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-[26rem]">
          {/* ── G — Rotating conic gradient ring + D — glass card + F — hover-breath ── */}
          <CardWithRing reducedMotion={reducedMotion}>
            <form onSubmit={submit} className="space-y-6 p-10">
              <BloomLogo className="h-7 w-auto text-[var(--brand-rose)]" />

              <div>
                <h1 className="font-heading text-[28px] font-bold leading-tight tracking-tight text-[var(--brand-ink)]">
                  Welcome back
                </h1>
                <p className="mt-1.5 text-sm text-[var(--brand-muted)]">Sign in to manage Bloom.</p>
              </div>

              <div className="space-y-3.5">
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-sm font-medium text-[var(--brand-ink)]"
                  >
                    Email
                  </label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@bloom.tv"
                    className="h-11 rounded-xl border-[var(--brand-border)] bg-white/60 text-[var(--brand-ink)] placeholder:text-[var(--brand-muted)]/60 focus-visible:border-[var(--brand-rose)] focus-visible:ring-2 focus-visible:ring-[var(--brand-rose)]/20"
                  />
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="mb-1.5 block text-sm font-medium text-[var(--brand-ink)]"
                  >
                    Password
                  </label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="h-11 rounded-xl border-[var(--brand-border)] bg-white/60 text-[var(--brand-ink)] placeholder:text-[var(--brand-muted)]/60 focus-visible:border-[var(--brand-rose)] focus-visible:ring-2 focus-visible:ring-[var(--brand-rose)]/20"
                  />
                </div>
              </div>

              {error ? (
                <p role="alert" className="text-sm text-[var(--brand-rose)]">
                  {error}
                </p>
              ) : null}

              <Button
                type="submit"
                className="h-11 w-full rounded-xl bg-[var(--brand-rose)] text-white shadow-[0_10px_30px_-12px_rgba(233,75,122,0.55)] transition-colors hover:bg-[var(--brand-rose)]/90"
                disabled={submitting || pending !== null}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Sign in"}
              </Button>
            </form>
          </CardWithRing>

          {isDev ? (
            <div className="mt-5 text-center">
              <button
                type="button"
                onClick={() => setShowDemo((v) => !v)}
                className="text-xs font-medium text-[var(--brand-muted)] transition-colors hover:text-[var(--brand-rose)]"
              >
                {showDemo ? "Hide demo logins" : "Show demo logins"}
              </button>
              {showDemo ? (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-[var(--brand-muted)]">
                  {DEMO_ROLES.map((d, i) => (
                    <span key={d.role} className="contents">
                      {i > 0 ? <span className="text-[var(--brand-border)]">·</span> : null}
                      <button
                        type="button"
                        onClick={() => onDemoClick(d.role)}
                        disabled={submitting || pending !== null}
                        className={cn(
                          "underline decoration-dotted underline-offset-4 transition-colors hover:text-[var(--brand-rose)] disabled:opacity-40",
                          pending === d.role && "text-[var(--brand-rose)]",
                        )}
                      >
                        {pending === d.role ? (
                          <span className="inline-flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                            {d.label}
                          </span>
                        ) : (
                          d.label
                        )}
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── G + D + F bundle ───────────────────────────────────────────────────────
// Wraps the form in:
//   • A slowly-rotating conic gradient halo (G).
//   • A glassmorphism inner panel (D) — bg-white/70 + backdrop-blur.
//   • A hover-driven scale 1→1.005 (F) — interactive feedback without
//     calling attention to itself.
function CardWithRing({
  children,
  reducedMotion,
}: {
  children: React.ReactNode;
  reducedMotion: boolean;
}) {
  const hoverRef = useRef<HTMLDivElement>(null);
  return (
    <motion.div
      ref={hoverRef}
      whileHover={reducedMotion ? undefined : { scale: 1.005 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="relative"
    >
      {/* Rotating conic-gradient halo. Sits at -2px on all sides, blurred so
          it reads as a soft glow at the rim — not a hard line. */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -inset-[2px] rounded-[30px]"
        style={{
          background:
            "conic-gradient(from 0deg, rgba(233,75,122,0.55), rgba(247,200,213,0.0) 25%, rgba(233,75,122,0.45) 50%, rgba(247,200,213,0.0) 75%, rgba(233,75,122,0.55))",
          filter: "blur(8px)",
        }}
        animate={reducedMotion ? { rotate: 0 } : { rotate: 360 }}
        transition={
          reducedMotion ? { duration: 0 } : { duration: 30, repeat: Infinity, ease: "linear" }
        }
      />
      {/* Glass card — translucent + blurred backdrop. Soft shadow for lift. */}
      <div className="relative overflow-hidden rounded-[28px] border border-white/40 bg-white/70 shadow-[0_40px_80px_-32px_rgba(233,75,122,0.28)] backdrop-blur-xl">
        {children}
      </div>
    </motion.div>
  );
}
